/**
 * Reference-Based Ancestry Inference
 *
 * Uses 1000 Genomes Phase 3 individual-level genotype data (2,504 individuals
 * from 26 populations) to perform k-NN + empirical EM hybrid ancestry inference.
 *
 * Algorithm:
 * 1. k-NN Phase: Find most similar reference individuals via IBS similarity
 * 2. Empirical EM: Use genotype counts from reference panel instead of HWE
 * 3. Combine: Use k-NN distribution as prior, refine with EM
 */

import type { ParsedSNPFile } from './snpFileParser';
import type { AncestryComposition } from '@/types/genome';
import type {
  InferencePopulation,
  AncestryInferenceResult,
} from './ancestryInference';
import aimsData from './ancestryAIMs_expanded.json';

// 26 sub-populations in 1000 Genomes Phase 3
export type SubPopulation =
  // EUR
  | 'CEU'
  | 'TSI'
  | 'FIN'
  | 'GBR'
  | 'IBS'
  // AFR
  | 'YRI'
  | 'LWK'
  | 'GWD'
  | 'MSL'
  | 'ESN'
  | 'ASW'
  | 'ACB'
  // EAS
  | 'CHB'
  | 'JPT'
  | 'CHS'
  | 'CDX'
  | 'KHV'
  // SAS
  | 'GIH'
  | 'PJL'
  | 'BEB'
  | 'STU'
  | 'ITU'
  // AMR
  | 'MXL'
  | 'PUR'
  | 'CLM'
  | 'PEL';

// Map sub-populations to super-populations
export const SUB_TO_SUPER_POP: Record<SubPopulation, InferencePopulation> = {
  CEU: 'EUR',
  TSI: 'EUR',
  FIN: 'EUR',
  GBR: 'EUR',
  IBS: 'EUR',
  YRI: 'AFR',
  LWK: 'AFR',
  GWD: 'AFR',
  MSL: 'AFR',
  ESN: 'AFR',
  ASW: 'AFR',
  ACB: 'AFR',
  CHB: 'EAS',
  JPT: 'EAS',
  CHS: 'EAS',
  CDX: 'EAS',
  KHV: 'EAS',
  GIH: 'SAS',
  PJL: 'SAS',
  BEB: 'SAS',
  STU: 'SAS',
  ITU: 'SAS',
  MXL: 'AMR',
  PUR: 'AMR',
  CLM: 'AMR',
  PEL: 'AMR',
};

// Display names for sub-populations
export const SUB_POP_NAMES: Record<SubPopulation, string> = {
  CEU: 'Utah European (CEU)',
  TSI: 'Tuscan Italian (TSI)',
  FIN: 'Finnish (FIN)',
  GBR: 'British (GBR)',
  IBS: 'Iberian Spanish (IBS)',
  YRI: 'Yoruba Nigerian (YRI)',
  LWK: 'Luhya Kenyan (LWK)',
  GWD: 'Gambian (GWD)',
  MSL: 'Mende Sierra Leonean (MSL)',
  ESN: 'Esan Nigerian (ESN)',
  ASW: 'African American SW (ASW)',
  ACB: 'African Caribbean (ACB)',
  CHB: 'Han Chinese Beijing (CHB)',
  JPT: 'Japanese (JPT)',
  CHS: 'Han Chinese South (CHS)',
  CDX: 'Dai Chinese (CDX)',
  KHV: 'Vietnamese (KHV)',
  GIH: 'Gujarati Indian (GIH)',
  PJL: 'Punjabi Pakistani (PJL)',
  BEB: 'Bengali (BEB)',
  STU: 'Sri Lankan Tamil (STU)',
  ITU: 'Telugu Indian (ITU)',
  MXL: 'Mexican (MXL)',
  PUR: 'Puerto Rican (PUR)',
  CLM: 'Colombian (CLM)',
  PEL: 'Peruvian (PEL)',
};

// Nearest neighbor info
export interface NearestNeighbor {
  id: string;
  population: SubPopulation;
  superPopulation: InferencePopulation;
  similarity: number;
}

// Extended result type with sub-population detail
export interface ReferencePanelResult extends AncestryInferenceResult {
  // Detailed 26 sub-population proportions
  subPopulationProportions: Record<SubPopulation, number>;

  // Top k nearest neighbors
  nearestNeighbors: NearestNeighbor[];

  // Method used
  method: 'knn' | 'empirical-em' | 'hybrid';
}

// Reference panel data structures
interface Reference1000GPanel {
  metadata: {
    version: string;
    buildDate: string;
    totalIndividuals: number;
    populations: Record<
      string,
      { code: string; superPop: string; count: number }
    >;
    sampleIds: string[];
    rsids: string[];
    totalMarkers: number;
  };
  genotypes: Record<string, string>;
}

interface ReferenceMetadata {
  version: string;
  buildDate: string;
  totalIndividuals: number;
  totalMarkers: number;
  populations: Record<
    string,
    { code: string; superPop: string; count: number; description: string }
  >;
  superPopulations: Record<
    string,
    { code: string; populations: string[]; totalCount: number }
  >;
  sampleInfo: Array<{
    id: string;
    population: string;
    superPopulation: string;
    index: number;
  }>;
}

// Lazy-loaded reference panel
let referencePanel: Reference1000GPanel | null = null;
let referenceMetadata: ReferenceMetadata | null = null;
let panelLoadPromise: Promise<void> | null = null;

// Type the AIMs data
const aims = aimsData as {
  metadata: {
    description: string;
    source: string;
    buildVersion: string;
    populations: Record<string, string>;
    totalMarkers: number;
  };
  markers: Array<{
    rsid: string;
    chromosome: string;
    position: number;
    ref: string;
    alt: string;
    frequencies: Record<InferencePopulation, number>;
  }>;
};

/**
 * Check if reference panel is available (non-placeholder)
 */
export async function isReferencePanelAvailable(): Promise<boolean> {
  try {
    // Check if panel has real data (not just the placeholder)
    const panelModule = await import('./reference1000G.json');
    return (
      panelModule &&
      panelModule.default &&
      panelModule.default.metadata &&
      panelModule.default.metadata.version !== 'PLACEHOLDER' &&
      panelModule.default.metadata.totalMarkers > 0
    );
  } catch {
    return false;
  }
}

/**
 * Load reference panel data (lazy loading)
 */
export async function loadReferencePanel(): Promise<{
  panel: Reference1000GPanel;
  metadata: ReferenceMetadata;
}> {
  if (referencePanel && referenceMetadata) {
    return { panel: referencePanel, metadata: referenceMetadata };
  }

  if (panelLoadPromise) {
    await panelLoadPromise;
    return { panel: referencePanel!, metadata: referenceMetadata! };
  }

  panelLoadPromise = (async () => {
    try {
      const [panelModule, metadataModule] = await Promise.all([
        import('./reference1000G.json'),
        import('./referenceMetadata.json'),
      ]);

      referencePanel = panelModule.default as Reference1000GPanel;
      referenceMetadata = metadataModule.default as ReferenceMetadata;

      // Check if this is a placeholder file
      if (referencePanel.metadata.version === 'PLACEHOLDER') {
        panelLoadPromise = null;
        throw new Error(
          'Reference panel not built. Run: npx tsx src/data/ancestry/build1000GenomesPanel.ts'
        );
      }
    } catch (_err) {
      panelLoadPromise = null;
      throw new Error(
        'Reference panel not found. Run: npx tsx src/data/ancestry/build1000GenomesPanel.ts'
      );
    }
  })();

  await panelLoadPromise;
  return { panel: referencePanel!, metadata: referenceMetadata! };
}

/**
 * Convert user genotype to dosage (0, 1, 2, or -1 for missing)
 */
function userGenotypeToDosage(
  genotype: string,
  refAllele: string,
  altAllele: string
): number {
  if (!genotype || genotype === '--' || genotype.includes('0')) {
    return -1; // Missing
  }

  const alleles = genotype.split('');
  if (alleles.length !== 2) {
    return -1;
  }

  let altCount = 0;
  for (const allele of alleles) {
    if (allele === altAllele) {
      altCount++;
    } else if (allele !== refAllele) {
      return -1; // Unknown allele
    }
  }

  return altCount;
}

/**
 * Calculate IBS (Identity-By-State) similarity between user and reference individual
 * Returns a weighted similarity score where informative markers count more
 */
function calculateIBSSimilarity(
  userDosages: Int8Array,
  refDosages: Int8Array,
  weights: Float32Array
): number {
  let similarity = 0;
  let totalWeight = 0;

  for (let i = 0; i < userDosages.length; i++) {
    const userDosage = userDosages[i];
    const refDosage = refDosages[i];

    if (userDosage < 0 || refDosage < 0 || refDosage > 2) {
      continue; // Skip missing data
    }

    const weight = weights[i];
    totalWeight += weight;

    // IBS score: 2 - |user - ref|
    // Perfect match = 2, heterozygote/homozygote mismatch = 1, opposite homozygotes = 0
    const diff = Math.abs(userDosage - refDosage);
    similarity += weight * (2 - diff);
  }

  if (totalWeight === 0) return 0;
  return similarity / (2 * totalWeight); // Normalize to [0, 1]
}

/**
 * Calculate informativeness weight for a marker based on frequency variance
 */
function calculateMarkerWeights(rsids: string[]): {
  weights: Float32Array;
  rsidToIndex: Map<string, number>;
} {
  const weights = new Float32Array(rsids.length);
  const rsidToIndex = new Map<string, number>();

  // Create marker lookup
  const markerMap = new Map<string, (typeof aims.markers)[0]>();
  for (const marker of aims.markers) {
    markerMap.set(marker.rsid, marker);
  }

  for (let i = 0; i < rsids.length; i++) {
    rsidToIndex.set(rsids[i], i);

    const marker = markerMap.get(rsids[i]);
    if (!marker) {
      weights[i] = 1.0;
      continue;
    }

    // Calculate variance of frequencies across populations
    const freqs = Object.values(marker.frequencies);
    const mean = freqs.reduce((a, b) => a + b, 0) / freqs.length;
    const variance =
      freqs.reduce((sum, f) => sum + (f - mean) ** 2, 0) / freqs.length;

    // Weight is standard deviation, capped at reasonable range
    weights[i] = Math.max(0.05, Math.min(0.5, Math.sqrt(variance)));
  }

  return { weights, rsidToIndex };
}

/**
 * Find k nearest neighbors using IBS similarity
 */
function findNearestNeighbors(
  userDosages: Int8Array,
  panel: Reference1000GPanel,
  metadata: ReferenceMetadata,
  weights: Float32Array,
  k: number
): NearestNeighbor[] {
  const { sampleIds, rsids } = panel.metadata;
  const n = sampleIds.length;

  // Calculate similarity to each reference individual
  const similarities: Array<{ index: number; similarity: number }> = [];

  for (let i = 0; i < n; i++) {
    // Extract reference individual's dosages
    const refDosages = new Int8Array(rsids.length);
    for (let j = 0; j < rsids.length; j++) {
      const genotypeStr = panel.genotypes[rsids[j]];
      const dosageChar = genotypeStr?.[i] ?? '9';
      refDosages[j] = dosageChar === '9' ? -1 : parseInt(dosageChar, 10);
    }

    const similarity = calculateIBSSimilarity(userDosages, refDosages, weights);
    similarities.push({ index: i, similarity });
  }

  // Sort by similarity (descending) and take top k
  similarities.sort((a, b) => b.similarity - a.similarity);

  return similarities.slice(0, k).map(s => {
    const sampleId = sampleIds[s.index];
    const sampleInfo = metadata.sampleInfo.find(info => info.id === sampleId)!;

    return {
      id: sampleId,
      population: sampleInfo.population as SubPopulation,
      superPopulation: sampleInfo.superPopulation as InferencePopulation,
      similarity: s.similarity,
    };
  });
}

/**
 * Calculate sub-population proportions from k-NN results
 */
function knnProportions(
  neighbors: NearestNeighbor[]
): Record<SubPopulation, number> {
  const counts: Record<string, number> = {};
  let totalWeight = 0;

  for (const neighbor of neighbors) {
    // Weight by similarity (higher similarity = more influence)
    const weight = neighbor.similarity ** 2;
    counts[neighbor.population] = (counts[neighbor.population] || 0) + weight;
    totalWeight += weight;
  }

  // Initialize all populations to 0
  const proportions: Record<SubPopulation, number> = {} as Record<
    SubPopulation,
    number
  >;
  for (const pop of Object.keys(SUB_TO_SUPER_POP) as SubPopulation[]) {
    proportions[pop] = 0;
  }

  // Normalize
  if (totalWeight > 0) {
    for (const [pop, count] of Object.entries(counts)) {
      proportions[pop as SubPopulation] = count / totalWeight;
    }
  }

  return proportions;
}

/**
 * Aggregate sub-population proportions to super-populations
 */
function aggregateToSuperPop(
  subProps: Record<SubPopulation, number>
): Record<InferencePopulation, number> {
  const superProps: Record<InferencePopulation, number> = {
    EUR: 0,
    AFR: 0,
    EAS: 0,
    SAS: 0,
    AMR: 0,
  };

  for (const [subPop, prop] of Object.entries(subProps) as [
    SubPopulation,
    number,
  ][]) {
    superProps[SUB_TO_SUPER_POP[subPop]] += prop;
  }

  return superProps;
}

/**
 * Empirical EM algorithm using reference panel genotype counts
 * instead of Hardy-Weinberg equilibrium assumptions
 */
function empiricalEM(
  userDosages: Int8Array,
  panel: Reference1000GPanel,
  metadata: ReferenceMetadata,
  rsidToIndex: Map<string, number>,
  _weights: Float32Array,
  options: {
    maxIterations?: number;
    convergenceThreshold?: number;
    priorProportions?: Record<SubPopulation, number>;
  } = {}
): Record<SubPopulation, number> {
  const {
    maxIterations = 100,
    convergenceThreshold = 0.0001,
    priorProportions,
  } = options;

  const populations = Object.keys(SUB_TO_SUPER_POP) as SubPopulation[];
  const K = populations.length;

  // Build population indices for each sample
  const popIndices: Map<SubPopulation, number[]> = new Map();
  for (const pop of populations) {
    popIndices.set(pop, []);
  }

  for (const sampleInfo of metadata.sampleInfo) {
    const pop = sampleInfo.population as SubPopulation;
    popIndices.get(pop)?.push(sampleInfo.index);
  }

  // Identify usable markers (user has genotype)
  const usableMarkers: Array<{ rsid: string; markerIndex: number }> = [];
  for (const [rsid, markerIndex] of rsidToIndex.entries()) {
    if (userDosages[markerIndex] >= 0) {
      usableMarkers.push({ rsid, markerIndex });
    }
  }

  if (usableMarkers.length === 0) {
    // Return uniform if no markers
    const uniformProp = 1 / K;
    const result: Record<SubPopulation, number> = {} as Record<
      SubPopulation,
      number
    >;
    for (const pop of populations) {
      result[pop] = uniformProp;
    }
    return result;
  }

  // Pre-compute empirical genotype probabilities for each marker and population
  // P(genotype | population) = (count + 0.5) / (n + 1.5) (Laplace smoothing)
  const markerLikelihoods: Array<Float32Array> = [];

  for (const { rsid, markerIndex } of usableMarkers) {
    const userDosage = userDosages[markerIndex];
    const genotypeStr = panel.genotypes[rsid];
    const popLikelihoods = new Float32Array(K);

    for (let k = 0; k < K; k++) {
      const pop = populations[k];
      const indices = popIndices.get(pop)!;
      const n = indices.length;

      if (n === 0) {
        popLikelihoods[k] = 1 / 3; // Uniform if no samples
        continue;
      }

      // Count how many reference individuals have user's genotype
      let matchCount = 0;
      for (const idx of indices) {
        const refDosageChar = genotypeStr?.[idx] ?? '9';
        if (
          refDosageChar !== '9' &&
          parseInt(refDosageChar, 10) === userDosage
        ) {
          matchCount++;
        }
      }

      // Laplace smoothed probability
      popLikelihoods[k] = (matchCount + 0.5) / (n + 1.5);
    }

    markerLikelihoods.push(popLikelihoods);
  }

  // Initialize proportions (from prior or uniform)
  let proportions = new Float64Array(K);
  if (priorProportions) {
    for (let k = 0; k < K; k++) {
      proportions[k] = priorProportions[populations[k]] || 1 / K;
    }
  } else {
    proportions.fill(1 / K);
  }

  // EM iterations
  const responsibilities = new Float64Array(K);

  for (let iter = 0; iter < maxIterations; iter++) {
    const newProportions = new Float64Array(K).fill(0);

    for (const popLikelihoods of markerLikelihoods) {
      // E-step: compute responsibilities
      let totalResp = 0;
      for (let k = 0; k < K; k++) {
        responsibilities[k] = popLikelihoods[k] * proportions[k];
        totalResp += responsibilities[k];
      }

      // Normalize and accumulate
      if (totalResp > 0) {
        for (let k = 0; k < K; k++) {
          newProportions[k] += responsibilities[k] / totalResp;
        }
      }
    }

    // M-step: normalize
    let total = 0;
    for (let k = 0; k < K; k++) {
      total += newProportions[k];
    }
    for (let k = 0; k < K; k++) {
      newProportions[k] /= total;
    }

    // Check convergence
    let maxDiff = 0;
    for (let k = 0; k < K; k++) {
      maxDiff = Math.max(maxDiff, Math.abs(newProportions[k] - proportions[k]));
    }

    proportions = newProportions;

    if (maxDiff < convergenceThreshold) {
      break;
    }
  }

  // Convert to record
  const result: Record<SubPopulation, number> = {} as Record<
    SubPopulation,
    number
  >;
  for (let k = 0; k < K; k++) {
    result[populations[k]] = proportions[k];
  }

  return result;
}

/**
 * Main inference function using reference panel
 */
export async function inferWithReferencePanel(
  parsedFile: ParsedSNPFile,
  options: {
    k?: number;
    method?: 'knn' | 'empirical-em' | 'hybrid';
  } = {}
): Promise<ReferencePanelResult> {
  const { k = 100, method = 'hybrid' } = options;

  // Load reference panel
  const { panel, metadata } = await loadReferencePanel();
  const { rsids } = panel.metadata;

  // Calculate marker weights and build index
  const { weights, rsidToIndex } = calculateMarkerWeights(rsids);

  // Build marker lookup
  const markerMap = new Map<string, (typeof aims.markers)[0]>();
  for (const marker of aims.markers) {
    markerMap.set(marker.rsid, marker);
  }

  // Convert user genotypes to dosage array
  const userDosages = new Int8Array(rsids.length);
  let usableCount = 0;

  for (let i = 0; i < rsids.length; i++) {
    const rsid = rsids[i];
    const userSNP = parsedFile.snps.get(rsid);
    const marker = markerMap.get(rsid);

    if (userSNP && marker) {
      const dosage = userGenotypeToDosage(
        userSNP.genotype,
        marker.ref,
        marker.alt
      );
      userDosages[i] = dosage;
      if (dosage >= 0) usableCount++;
    } else {
      userDosages[i] = -1;
    }
  }

  // Find nearest neighbors
  const nearestNeighbors = findNearestNeighbors(
    userDosages,
    panel,
    metadata,
    weights,
    k
  );

  // Calculate proportions based on method
  let subPopulationProportions: Record<SubPopulation, number>;

  if (method === 'knn') {
    subPopulationProportions = knnProportions(nearestNeighbors);
  } else if (method === 'empirical-em') {
    subPopulationProportions = empiricalEM(
      userDosages,
      panel,
      metadata,
      rsidToIndex,
      weights
    );
  } else {
    // Hybrid: use k-NN as prior for EM
    const knnPrior = knnProportions(nearestNeighbors);
    subPopulationProportions = empiricalEM(
      userDosages,
      panel,
      metadata,
      rsidToIndex,
      weights,
      { priorProportions: knnPrior }
    );
  }

  // Aggregate to super-populations for backward compatibility
  const superPopProportions = aggregateToSuperPop(subPopulationProportions);

  // Determine confidence
  let confidence: 'high' | 'moderate' | 'low';
  if (usableCount >= 500) {
    confidence = 'high';
  } else if (usableCount >= 100) {
    confidence = 'moderate';
  } else {
    confidence = 'low';
  }

  // Build composition for display
  const composition: AncestryComposition[] = (
    Object.entries(superPopProportions) as [InferencePopulation, number][]
  )
    .map(([pop, proportion]) => ({
      population: pop.toLowerCase() as never,
      category: {
        EUR: 'european',
        AFR: 'african',
        EAS: 'east_asian',
        SAS: 'south_asian',
        AMR: 'native_american',
      }[pop] as AncestryComposition['category'],
      percentage: Math.round(proportion * 1000) / 10,
      displayName: {
        EUR: 'European',
        AFR: 'African',
        EAS: 'East Asian',
        SAS: 'South Asian',
        AMR: 'Native American',
      }[pop],
    }))
    .filter(c => c.percentage >= 0.5)
    .sort((a, b) => b.percentage - a.percentage);

  return {
    proportions: superPopProportions,
    subPopulationProportions,
    nearestNeighbors,
    confidence,
    markersUsed: usableCount,
    markersAvailable: rsids.length,
    composition,
    method,
  };
}

/**
 * Get detailed sub-population composition for display
 */
export function getSubPopulationComposition(
  subProps: Record<SubPopulation, number>
): Array<{
  population: SubPopulation;
  superPopulation: InferencePopulation;
  percentage: number;
  displayName: string;
}> {
  return (Object.entries(subProps) as [SubPopulation, number][])
    .filter(([_, prop]) => prop >= 0.005) // At least 0.5%
    .map(([pop, prop]) => ({
      population: pop,
      superPopulation: SUB_TO_SUPER_POP[pop],
      percentage: Math.round(prop * 1000) / 10,
      displayName: SUB_POP_NAMES[pop],
    }))
    .sort((a, b) => b.percentage - a.percentage);
}

// Map super-populations to their sub-populations
const SUPER_TO_SUB_POPS: Record<InferencePopulation, SubPopulation[]> = {
  EUR: ['CEU', 'TSI', 'FIN', 'GBR', 'IBS'],
  AFR: ['YRI', 'LWK', 'GWD', 'MSL', 'ESN', 'ASW', 'ACB'],
  EAS: ['CHB', 'JPT', 'CHS', 'CDX', 'KHV'],
  SAS: ['GIH', 'PJL', 'BEB', 'STU', 'ITU'],
  AMR: ['MXL', 'PUR', 'CLM', 'PEL'],
};

// Map category names to super-populations
const CATEGORY_TO_SUPER_POP: Record<string, InferencePopulation> = {
  european: 'EUR',
  african: 'AFR',
  east_asian: 'EAS',
  south_asian: 'SAS',
  native_american: 'AMR',
};

/**
 * Refine ancestry segments with sub-population assignments using the reference panel.
 * For each segment, calculates similarity to reference individuals within that
 * super-population and assigns the most likely sub-population.
 */
export async function refineSegmentsWithSubPopulations(
  segments: import('@/types/genome').AncestrySegment[],
  parsedFile: import('./snpFileParser').ParsedSNPFile
): Promise<import('@/types/genome').AncestrySegment[]> {
  // Check if reference panel is available
  const panelAvailable = await isReferencePanelAvailable();
  if (!panelAvailable) {
    return segments; // Return unchanged if no panel
  }

  const { panel, metadata } = await loadReferencePanel();
  const { rsids, sampleIds: _sampleIds } = panel.metadata;

  // Build marker lookup by chromosome and position
  const markerMap = new Map<string, (typeof aims.markers)[0]>();
  const markersByChrom = new Map<string, typeof aims.markers>();

  for (const marker of aims.markers) {
    markerMap.set(marker.rsid, marker);
    if (!markersByChrom.has(marker.chromosome)) {
      markersByChrom.set(marker.chromosome, []);
    }
    markersByChrom.get(marker.chromosome)!.push(marker);
  }

  // Sort markers by position within each chromosome
  for (const markers of markersByChrom.values()) {
    markers.sort((a, b) => a.position - b.position);
  }

  // Build rsid to index mapping
  const rsidToIndex = new Map<string, number>();
  for (let i = 0; i < rsids.length; i++) {
    rsidToIndex.set(rsids[i], i);
  }

  // Build sample index by population
  const samplesByPop = new Map<SubPopulation, number[]>();
  for (const info of metadata.sampleInfo) {
    const pop = info.population as SubPopulation;
    if (!samplesByPop.has(pop)) {
      samplesByPop.set(pop, []);
    }
    samplesByPop.get(pop)!.push(info.index);
  }

  // Process each segment
  const refinedSegments: import('@/types/genome').AncestrySegment[] = [];

  for (const segment of segments) {
    // Get the super-population for this segment
    const superPop = CATEGORY_TO_SUPER_POP[segment.category];
    if (!superPop) {
      refinedSegments.push(segment);
      continue;
    }

    // Get sub-populations to compare against
    const subPops = SUPER_TO_SUB_POPS[superPop];
    if (!subPops || subPops.length === 0) {
      refinedSegments.push(segment);
      continue;
    }

    // Find markers in this segment's region
    const chromMarkers = markersByChrom.get(segment.chromosome) || [];
    const segmentMarkers = chromMarkers.filter(
      m => m.position >= segment.start && m.position <= segment.end
    );

    if (segmentMarkers.length < 3) {
      // Not enough markers to refine
      refinedSegments.push(segment);
      continue;
    }

    // Get user dosages for segment markers
    const userDosages: number[] = [];
    const markerIndices: number[] = [];

    for (const marker of segmentMarkers) {
      const userSNP = parsedFile.snps.get(marker.rsid);
      const panelIndex = rsidToIndex.get(marker.rsid);

      if (userSNP && panelIndex !== undefined) {
        const dosage = userGenotypeToDosage(
          userSNP.genotype,
          marker.ref,
          marker.alt
        );
        if (dosage >= 0) {
          userDosages.push(dosage);
          markerIndices.push(panelIndex);
        }
      }
    }

    if (userDosages.length < 3) {
      refinedSegments.push(segment);
      continue;
    }

    // Calculate similarity to each sub-population
    const subPopScores: Array<{ pop: SubPopulation; score: number }> = [];

    for (const subPop of subPops) {
      const sampleIndices = samplesByPop.get(subPop) || [];
      if (sampleIndices.length === 0) continue;

      let totalSimilarity = 0;
      let sampleCount = 0;

      // Sample up to 20 individuals for efficiency
      const samplesToCheck =
        sampleIndices.length <= 20
          ? sampleIndices
          : sampleIndices
              .slice()
              .sort(() => Math.random() - 0.5)
              .slice(0, 20);

      for (const sampleIdx of samplesToCheck) {
        let matches = 0;
        let comparisons = 0;

        for (let i = 0; i < markerIndices.length; i++) {
          const panelIdx = markerIndices[i];
          const rsid = rsids[panelIdx];
          const genotypeStr = panel.genotypes[rsid];

          if (genotypeStr) {
            const refDosageChar = genotypeStr[sampleIdx];
            if (refDosageChar !== '9') {
              const refDosage = parseInt(refDosageChar, 10);
              // IBS similarity: 2 - |diff|
              matches += 2 - Math.abs(userDosages[i] - refDosage);
              comparisons += 2;
            }
          }
        }

        if (comparisons > 0) {
          totalSimilarity += matches / comparisons;
          sampleCount++;
        }
      }

      if (sampleCount > 0) {
        subPopScores.push({
          pop: subPop,
          score: totalSimilarity / sampleCount,
        });
      }
    }

    // Find best sub-population
    if (subPopScores.length > 0) {
      subPopScores.sort((a, b) => b.score - a.score);
      const best = subPopScores[0];

      // Calculate confidence as ratio of best to second best
      let subPopConfidence = best.score;
      if (subPopScores.length > 1) {
        const secondBest = subPopScores[1];
        // Confidence is higher when best is clearly better than second
        subPopConfidence = Math.min(1, best.score / (secondBest.score + 0.001));
      }

      refinedSegments.push({
        ...segment,
        subPopulation: best.pop,
        subPopulationName: SUB_POP_NAMES[best.pop],
        subPopulationConfidence: subPopConfidence,
      });
    } else {
      refinedSegments.push(segment);
    }
  }

  return refinedSegments;
}
